import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { BadgeCheck, Calendar, Building, Wallet, FolderUp, FileText, AlertTriangle } from "lucide-react";

const STATUS_LABEL = {
  CRA_SUBMITTED: { label: "CRA soumis", color: "bg-blue-100 text-blue-800" },
  CRA_APPROVED: { label: "CRA validated", color: "bg-emerald-100 text-emerald-800" },
  INVOICE_TO_SUBMIT: { label: "Invoice to submit", color: "bg-brand-orange-100 text-brand-orange-600" },
  INVOICE_UNDER_REVIEW: { label: "Invoice in validation", color: "bg-amber-100 text-amber-800" },
  INVOICE_PROCESSING: { label: "Invoice processing", color: "bg-violet-100 text-violet-800" },
  INVOICE_PAID: { label: "Invoice paid", color: "bg-green-100 text-green-800" },
  INVOICE_REJECTED: { label: "Invoice rejected", color: "bg-red-100 text-red-800" },
  NOT_STARTED: { label: "En attente", color: "bg-zinc-100 text-zinc-600" },
};

export default function FreelanceMyProfile() {
  const [data, setData] = useState(null);

  const load = () => api.get("/freelance/me").then((r) => setData(r.data)).catch(() => setData({ error: true }));
  useEffect(() => { load(); }, []);

  if (!data) return <div className="p-12 text-zinc-500">Loading…</div>;
  if (data.error) return <div className="p-12 text-red-600">You are not a freelance profile.</div>;

  const fp = data.freelance_profile;
  const renewalSoon = data.days_until_end !== null && data.days_until_end <= 30 && data.days_until_end >= 0;
  const expired = data.days_until_end !== null && data.days_until_end < 0;

  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-[1100px]">
      <div className="text-[11px] uppercase tracking-[0.25em] text-brand-navy-500 mb-3 font-mono">Freelance</div>
      <h1 className="font-heading text-4xl font-bold tracking-tight mb-2 flex items-center gap-3">
        <BadgeCheck size={28} className="text-brand-orange" /> Infos professionnelles
      </h1>
      <p className="text-sm text-zinc-600 mb-8 max-w-2xl">
        Vos informations contractuelles sont gérées par le CEO. Pour toute modification, contactez-le directement.
        Le contrat signé est uploaded par votre référent BAIM ; vous voyez ici son statut.
      </p>

      {(renewalSoon || expired) && (
        <div className="mb-6 p-4 rounded-lg border bg-amber-50 border-amber-300 text-amber-900 flex items-center gap-3" data-testid="renewal-banner">
          <AlertTriangle size={18} />
          <div className="text-sm">
            {expired
              ? <>Votre contrat est <strong>expired</strong> depuis {Math.abs(data.days_until_end)} jour(s).</>
              : <>Votre contrat se termine dans <strong>{data.days_until_end}</strong> day(s) — remember to contact the CEO for renewal.</>}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <Field icon={Wallet} label="TJM HT" value={`${fp.tjm_ht} ${fp.currency}`} testid="field-tjm" />
        <Field icon={Building} label="Company de portage" value={fp.portage_company_name || "—"} testid="field-portage" />
        <Field icon={Calendar} label="Contract start" value={fp.contract_start} testid="field-start" />
        <Field icon={Calendar} label="Fin de contrat" value={fp.contract_end} testid="field-end" />
      </div>

      <div className="bg-white border border-brand-navy/15 rounded-lg p-6" data-testid="contract-status-card">
        <div className="flex items-center gap-3 mb-4">
          <FolderUp size={18} className="text-brand-navy" />
          <h2 className="font-heading font-bold text-lg">My signed contract</h2>
        </div>
        {fp.contract_received ? (
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-100 text-emerald-800 rounded-full text-sm font-medium" data-testid="contract-status-received">
            <FileText size={14} /> Reçu
          </div>
        ) : (
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-100 text-amber-800 rounded-full text-sm font-medium" data-testid="contract-status-not-received">
            <AlertTriangle size={14} /> Non reçu
          </div>
        )}
        <p className="text-[11px] text-zinc-500 mt-3 font-mono">
          {fp.contract_received
            ? "The signed contract has been archived by BAIM in your admin folder."
            : "If you signed it, forward it to your BAIM contact (CEO or Gestionnaire) — they will upload it into your dedicated folder."}
        </p>
      </div>

      <div className="mt-8">
        <Link to="/freelance/me/invoices" className="text-sm text-brand-navy hover:underline" data-testid="goto-my-invoices">
          → View « Mes factures »
        </Link>
      </div>
    </div>
  );
}

function Field({ icon: Icon, label, value, testid }) {
  return (
    <div className="bg-white border border-brand-navy/15 rounded-lg p-5" data-testid={testid}>
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-mono mb-3">
        <Icon size={13} /> {label}
      </div>
      <div className="font-heading text-xl font-semibold tracking-tight">{value}</div>
    </div>
  );
}

export { STATUS_LABEL };
